import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { pollPaymentStatus, resumePayment } from '@maestra/core/store/slices/subscription';

import { message } from '../../ui-carbon/padroes/avisos';
import { useAlterarPendente } from './assinaturaPendente';

// O PAGAMENTO PIX (/pagamento), sem tela. Cópia literal de `pages/Payment`:
//  - sem QR no estado, RETOMA a cobrança em aberto (uma vez) antes de desistir; `active` só vira
//    sucesso sem renovação pendente (a combinação que já mostrou "pago" sem pagamento);
//  - cartão em análise não tem QR: espera a operadora;
//  - o QR expira (a contagem só aparece dentro de 48h, porque a Asaas devolve validade de um ano);
//  - a espera pelo pagamento, com a conexão perdida separada do tempo esgotado;
//  - SEMPRE COM SAÍDA (04/10/2026): "Alterar plano ou pagamento" descarta a assinatura pendente
//    no servidor (que confere na Asaas que nada caiu) e volta ao pagamento com as escolhas de antes.
//    É o caminho de quem tem o PIX do banco com erro e quer o cartão, ou escolheu perfis errado;
//  - o QR que não volta é "não deu para abrir" (com tentar de novo), e não "expirou": a falha podia
//    ser só um instante da Asaas, e o botão dali cancelava uma assinatura ainda pagável;
//  - o QR vencido (o do Pix Automático vale 24h) leva a escolher de novo, sem confirmação: ele já
//    não serve. O servidor também descarta sozinho o que venceu (asaas-resume-payment, webhook);
//  - a MENSALIDADE EM ATRASO no cartão ou no Pix Automático (o "Regularizar pagamento" do painel)
//    não tem QR nosso: a tela leva à fatura da Asaas e espera o pagamento cair. Antes dizia "em
//    análise" (cartão) ou "pago" (Pix Automático) a quem devia.

export function formatarContagem(seconds: number, expiresAt?: string | null): string {
  if (seconds <= 0) return '00:00';
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins ? `${hours}h${String(mins).padStart(2, '0')}` : `${hours}h`;
  }
  const date = expiresAt ? new Date(expiresAt) : new Date(Date.now() + seconds * 1000);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export type EstadoDoPix = 'retomando' | 'cartaoEmAnalise' | 'faturaEmAtraso' | 'naoAbriu' | 'venceu' | 'pago' | 'semTempo' | 'semConexao' | 'aguardando' | 'nada';

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const usePagamentoPix = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pixData, status, pendingRenewal, pixAutomatic: pixAutomaticGlobal } = useAppSelector((s) => s.subscription);
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [renewal, setRenewal] = useState(false);
  const [pixAutomaticResume, setPixAutomaticResume] = useState(false);
  const pixAutomatic = pixAutomaticGlobal || pixAutomaticResume;
  const [timedOut, setTimedOut] = useState(false);
  const [connectivityError, setConnectivityError] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [resumeFailed, setResumeFailed] = useState(false);
  const [cardAnalysis, setCardAnalysis] = useState(false);
  const [fatura, setFatura] = useState<{ url: string; automatico: boolean; valor: number | null } | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const alterar = useAlterarPendente();
  const pollingStarted = useRef(false);
  // A espera em curso: parada ao sair da tela.
  const esperaRef = useRef<{ abort: () => void } | null>(null);
  const resumeTried = useRef(-1);

  useEffect(() => {
    if (paymentConfirmed) return;
    // O QR basta: sem a validade, a tela mostra o código sem a contagem (antes girava para sempre).
    if (pixData?.qrCode) return;
    if (resumeTried.current === tentativa) return;
    resumeTried.current = tentativa;
    setResumeFailed(false);
    setResuming(true);
    dispatch(resumePayment())
      .unwrap()
      .then((res) => {
        setResuming(false);
        if (res.status === 'active' && !res.pendingRenewal) { setPaymentConfirmed(true); return; }
        if (res.status === 'none') { navigate('/planos', { replace: true }); return; }
        if (res.status === 'overdue' && res.invoiceUrl) {
          setFatura({ url: res.invoiceUrl, automatico: !!res.pixAutomatic, valor: res.value ?? null });
          return;
        }
        if (res.billingType === 'CREDIT_CARD') { setCardAnalysis(true); return; }
        if (res.pendingRenewal) setRenewal(true);
        if (res.pixAutomatic) setPixAutomaticResume(true);
        if (!res.pixData?.qrCode) setResumeFailed(true);
      })
      .catch(() => {
        setResuming(false);
        navigate('/planos', { replace: true });
      });
  }, [pixData, paymentConfirmed, status, dispatch, navigate, tentativa]);

  // Cartão em análise e mensalidade na fatura: espera enquanto a tela estiver aberta ("esta tela
  // atualiza sozinha"), recomeçando a cada 10 minutos, e para ao sair.
  useEffect(() => {
    if (!cardAnalysis && !fatura) return undefined;
    let alive = true;
    let atual: { abort: () => void } | null = null;
    const rodar = () => {
      const p = dispatch(pollPaymentStatus());
      atual = p;
      p.unwrap()
        .then(() => { if (alive) setPaymentConfirmed(true); })
        .catch((e: unknown) => {
          if (!alive || e === 'cancelado') return;
          if (e === 'sem_assinatura') {
            // No cartão, a assinatura some quando a operadora recusa (o servidor a descarta).
            if (cardAnalysis) message.warning('A operadora não aprovou o pagamento. Tente outro cartão ou o PIX.');
            navigate('/planos', { replace: true });
            return;
          }
          setTimeout(() => { if (alive) rodar(); }, 5000);
        });
    };
    rodar();
    return () => { alive = false; atual?.abort(); };
  }, [cardAnalysis, fatura, dispatch, navigate]);

  useEffect(() => {
    if (!pixData?.expiresAt) return undefined;
    const calculateRemaining = () => Math.max(0, Math.floor((new Date(pixData.expiresAt!).getTime() - Date.now()) / 1000));
    setSecondsRemaining(calculateRemaining());
    const interval = setInterval(() => {
      const remaining = calculateRemaining();
      setSecondsRemaining(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [pixData?.expiresAt]);

  const esperar = useCallback(() => {
    esperaRef.current?.abort();
    const p = dispatch(pollPaymentStatus());
    esperaRef.current = p;
    p.unwrap()
      .then(() => setPaymentConfirmed(true))
      .catch((errorMessage: unknown) => {
        const msg = typeof errorMessage === 'string' ? errorMessage : '';
        if (msg === 'cancelado' || (errorMessage as { name?: string })?.name === 'AbortError') return;
        // O servidor descartou a assinatura pendente (o PIX venceu): o QR na tela já não vale.
        if (msg === 'sem_assinatura') { navigate('/planos', { replace: true }); return; }
        if (msg.includes('Conexão perdida')) setConnectivityError(true);
        else setTimedOut(true);
      });
  }, [dispatch, navigate]);

  useEffect(() => () => esperaRef.current?.abort(), []);

  useEffect(() => {
    if (!pixData || !pixData.qrCode || pollingStarted.current) return;
    pollingStarted.current = true;
    esperar();
  }, [pixData, esperar]);

  useEffect(() => {
    if (!paymentConfirmed) return undefined;
    const timeout = setTimeout(() => navigate('/planos/sucesso', { replace: true }), 1500);
    return () => clearTimeout(timeout);
  }, [paymentConfirmed, navigate]);

  const ativoEmDia = status === 'active' && !renewal && !pendingRenewal;
  const temQr = !!pixData?.qrCode;
  const isExpired = secondsRemaining !== null && secondsRemaining <= 0;

  const estado: EstadoDoPix = resuming ? 'retomando'
    : fatura && !paymentConfirmed ? 'faturaEmAtraso'
      : cardAnalysis && !paymentConfirmed ? 'cartaoEmAnalise'
        : resumeFailed ? 'naoAbriu'
          : !temQr && !paymentConfirmed && !ativoEmDia ? 'nada'
            : paymentConfirmed || ativoEmDia ? 'pago'
              // Renovação vencida segue pagável (a Asaas aceita o PIX depois do vencimento); só o QR
              // da primeira assinatura vencido é que leva a escolher de novo.
              : isExpired && !renewal && !pendingRenewal ? 'venceu'
                : timedOut ? 'semTempo'
                  : connectivityError ? 'semConexao'
                    : pixData ? 'aguardando' : 'nada';

  const qr = pixData?.qrCode ? (pixData.qrCode.startsWith('data:') ? pixData.qrCode : `data:image/png;base64,${pixData.qrCode}`) : null;

  return {
    estado,
    automatico: pixAutomatic,
    qr,
    copiaECola: pixData?.copyPaste ?? '',
    // O prazo só aparece quando informa algo (dentro de 48h, ou já expirado).
    prazo: isExpired ? { expirado: true, texto: 'QR Code expirado' }
      : secondsRemaining !== null && secondsRemaining <= 48 * 3600
        ? { expirado: false, texto: `Expira em ${formatarContagem(secondsRemaining, pixData?.expiresAt)}` }
        : null,
    // Alterar só existe para a primeira assinatura: na renovação, a assinatura já está de pé (o
    // caminho é o painel dos planos).
    alterar: renewal || pendingRenewal ? null : alterar,
    abrirDeNovo: () => setTentativa((t) => t + 1),
    fatura: fatura && {
      url: fatura.url,
      titulo: fatura.automatico ? 'O débito automático desta mensalidade não passou' : 'A mensalidade no cartão não passou',
      texto: [
        `Pague${fatura.valor ? ` os ${brl(fatura.valor)}` : ''} pela fatura segura da Asaas${fatura.automatico ? ', com PIX ou cartão' : ', com o mesmo cartão ou outro'}.`,
        fatura.automatico ? 'Se preferir, deixe o saldo na conta: o banco tenta o débito de novo nos próximos dias, e na noite anterior a cada tentativa a fatura fica bloqueada até o débito.' : null,
        'Esta tela atualiza sozinha quando o pagamento cair.',
      ].filter(Boolean).join(' '),
    },
    tentarDeNovo: () => { setConnectivityError(false); setTimedOut(false); pollingStarted.current = false; esperar(); },
    voltarAoPainel: () => navigate('/artists', { replace: true }),
  };
};

export type DadosDoPix = ReturnType<typeof usePagamentoPix>;
